import { useContext, useMemo } from 'react';
import { PapiAttachmentFile, PapiDeviation } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import DeviationMediaService from '../../../utils/DeviationMediaService';
import AttachmentMediaService from '../../../utils/DeviationMediaService/AttachmentMediaService';
import { DeviantMediaWrapper } from '../../../utils/DeviationMediaService/types';

/** memoizes potentially expensive deviation media service values */
export function useMediaService(source: DeviantMediaWrapper) {
  const isMobile = useContext(MobileContext);
  const { device } = useContext(SessionContext);
  const attachment = (source as PapiAttachmentFile).fileId
    ? (source as PapiAttachmentFile)
    : undefined;
  const deviation = attachment ? undefined : (source as PapiDeviation);

  const isGif = useMemo(
    () =>
      deviation
        ? DeviationMediaService.isGif(deviation)
        : AttachmentMediaService.isGif(attachment!),
    [deviation, attachment]
  );

  const isPng = useMemo(
    () =>
      deviation
        ? DeviationMediaService.isPng(deviation)
        : AttachmentMediaService.isPng(attachment!),
    [deviation, attachment]
  );

  const hasSrc = useMemo(
    () =>
      deviation
        ? !!DeviationMediaService.getLargestFullview(deviation)
        : !!AttachmentMediaService.getLargestFullview(attachment!),
    [deviation, attachment]
  );

  const hasPreview = useMemo(
    () =>
      deviation
        ? !!DeviationMediaService.getDeviationFile(deviation, 'preview')
        : !!AttachmentMediaService.getMediaFile(attachment!, 'preview'),
    [deviation, attachment]
  );

  const isPreviewable = hasPreview && hasSrc;

  const screenVw = useMemo(
    () => (isMobile && !device?.isTablet ? '100vw' : '(min-width: 1152px) 70vw, 100vw'),
    [isMobile, device]
  );

  const previewSrcSet = useMemo(
    () =>
      isPreviewable
        ? deviation
          ? DeviationMediaService.getSrcSetForDeviationPreview(deviation, isMobile, screenVw)
          : AttachmentMediaService.getSrcSetForAtttachmentPreview(attachment!, isMobile, screenVw)
        : ({} as any),
    [deviation, attachment, isPreviewable, isMobile, screenVw]
  );

  const fullview = useMemo(
    () =>
      deviation
        ? DeviationMediaService.getDeviationFile(deviation, 'fullview')
        : AttachmentMediaService.getMediaFile(attachment!, 'fullview'),
    [deviation, attachment]
  );

  return {
    isPreviewable,
    isGif,
    isPng,
    previewSrcSet,
    fullview,
  };
}
